import { useContext, useMemo } from 'react'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { NewChat } from '@/assets/icons'
import { AuthContext } from '@/context/auth-context'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useNewChatMatterBoundAction } from '@/chat-common/new_chat/hooks/useNewChatMatterBoundAction'
import { useMatterChatHistory } from '@/chat-common/conversation_list/hooks/useMatterChatHistory'
import { useChatHistoryActions } from './hooks/useChatHistoryActions'
import { useChatHistoryVirtualizer } from './hooks/useChatHistoryVirtualizer'
import AgentV2ConversationMenu from '@/agent-v2/conversations/components/AgentV2ConversationMenu'
import AgentV2ConversationDialog from '@/agent-v2/conversations/components/AgentV2ConversationDialog'
import { ChatHistoryItem } from '../side-nav-item/ChatHistoryItem'
import { SideNavItem } from '../side-nav-item/SideNavItem'
import { matterPaths } from '@/routing/paths'

const kLoadingRowCount = 3
const kSidebarChatLimit = 20

export type ChatHistoryProps = {
  /** The active matter to list chats for */
  matterId: string
  /** The id of the currently open chat, for active-row highlighting */
  activeChatId?: string
  allowNewChat?: boolean
  /** Called after New Chat, a chat row, or View All is clicked (e.g. to close the mobile drawer) */
  onNavigate?: () => void
}

/**
 * The side nav's "Chat History" section: a "New Chat" trigger followed by a flat, unadorned list
 * of the current user's 20 most recent chats. Only the visible window and keyboard/menu anchors are mounted.
 * New Chat stays pinned while the chat list scrolls independently of the rest of the nav in its own
 * container. Fetches and owns its own chat list and loading state — callers only supply which
 * matter and which chat (if any) is currently open. New Chat is hidden while the initial fetch is
 * in flight, replaced (along with the chat rows) by loading-skeleton rows.
 */
export function ChatHistory({ matterId, activeChatId, onNavigate, allowNewChat = true }: ChatHistoryProps) {
  // Reset all section-local state when switching matters: scroll, focus, menus/dialogs,
  // and chat-creation status. This does not cancel in-flight creation requests.
  return <MatterChatHistory key={matterId} matterId={matterId} activeChatId={activeChatId} onNavigate={onNavigate} allowNewChat={allowNewChat} />
}

function MatterChatHistory({ matterId, activeChatId, onNavigate, allowNewChat }: ChatHistoryProps) {
  const { trackEvent } = useAnalytics()
  const authContext = useContext(AuthContext)
  const { items: allItems, isLoading } = useMatterChatHistory(matterId, authContext?.user?.uid)
  const items = useMemo(() => allItems.slice(0, kSidebarChatLimit), [allItems])

  const { handleButtonClick, unsentChatId, clickDisabled, createError } = useNewChatMatterBoundAction(onNavigate)

  const {
    menuOpenForId,
    setMenuOpenForId,
    renameChatItem,
    renameChatModalOpen,
    openRenameChatModal,
    closeRenameChatModal,
    deleteChatItem,
    deleteChatModalOpen,
    openDeleteChatModal,
    closeDeleteChatModal,
  } = useChatHistoryActions()

  const { scrollRef, newChatRef, rowsHeight, virtualRows, setFocusedChatId, handleRowBlur } = useChatHistoryVirtualizer({ items, isLoading, menuOpenForId })

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-1 px-2">
      <div className="flex h-7 shrink-0 items-center px-2 py-0.5">
        <span className="font-rethink-sans text-xs font-medium uppercase text-text-tertiary">Chat History</span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-0.5">
        {!isLoading && allowNewChat && (
          <div ref={newChatRef} className="shrink-0">
            <ChatHistoryItem
              data-testid="side-nav-new-chat"
              icon={NewChat}
              label="New Chat"
              isActive={!!activeChatId && activeChatId === unsentChatId}
              disabled={clickDisabled}
              onClick={handleButtonClick}
            />
          </div>
        )}

        <div
          ref={scrollRef}
          role="region"
          aria-label="Chat history"
          className="-mx-0.5 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-0.5 [scrollbar-color:theme(colors.brand-neutral.400)_transparent]"
        >
          {isLoading && Array.from({ length: kLoadingRowCount }, (_, i) => <ChatHistoryItem key={i} loading />)}
          {!isLoading && items.length > 0 && (
            <div className="relative shrink-0" style={{ height: rowsHeight }}>
              {virtualRows.map((virtualRow) => {
                const item = items[virtualRow.index]
                const isMenuOpen = menuOpenForId === item.id
                const isRowActive = item.id === activeChatId || isMenuOpen

                return (
                  <div
                    key={virtualRow.key}
                    className="absolute left-0 top-0 w-full"
                    style={{ transform: `translateY(${virtualRow.offset}px)` }}
                    onFocusCapture={() => setFocusedChatId(item.id)}
                    onBlurCapture={handleRowBlur}
                  >
                    <ChatHistoryItem
                      data-testid={`chat-history-item-${item.id}`}
                      to={item.href}
                      label={item.titlePlaceholder ?? item.title}
                      labelPending={item.titlePending}
                      isActive={item.id === activeChatId}
                      unread={item.unread}
                      onClick={() => {
                        trackEvent(AnalyticsEvent.OpenExistingConversation)
                        onNavigate?.()
                      }}
                      trailingAction={
                        <AgentV2ConversationMenu
                          conversationId={item.id}
                          unread={item.unread}
                          isRowActive={isRowActive}
                          onSelectDelete={() => openDeleteChatModal(item)}
                          onSelectRename={() => openRenameChatModal(item)}
                          onOpenChange={(open) => setMenuOpenForId(open ? item.id : null)}
                          open={isMenuOpen}
                          showTriggerOnHover
                          triggerTabIndex={-1}
                        />
                      }
                    />
                  </div>
                )
              })}
            </div>
          )}
          {!isLoading && items.length > 0 && (
            <div className="shrink-0 pb-2">
              <SideNavItem label="View All" variant="subtle" to={matterPaths.chats(matterId)} onClick={onNavigate} />
            </div>
          )}
        </div>
      </div>

      {createError && (
        <div className="shrink-0 pb-1">
          <InlineMessage variant="error">Error creating chat. Please try again.</InlineMessage>
        </div>
      )}

      <AgentV2ConversationDialog
        conversationId={renameChatItem?.id ?? null}
        kind="rename"
        open={renameChatModalOpen}
        onClose={closeRenameChatModal}
        // An untitled chat's row shows its first message, so the rename field is prefilled with that instead of "New Chat"
        displayedTitle={renameChatItem?.titlePlaceholder ?? undefined}
      />
      <AgentV2ConversationDialog conversationId={deleteChatItem?.id ?? null} kind="delete" open={deleteChatModalOpen} onClose={closeDeleteChatModal} />
    </div>
  )
}
